import { DecimalPipe, TitleCasePipe } from '@angular/common';
import { AfterViewChecked, AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MdDialog, MdDialogRef } from '@angular/material';
import { AuthorizeService } from 'app/services/authorize/authorize.service';
import { ProductService } from 'app/services/products/product.service';
import { UserService } from 'app/services/user/user.service';
import { UtilitiesService } from 'app/services/utilities/utilities.service';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Subscription } from 'rxjs/Subscription';
import { CartModel, CartMultipaymentDetail } from '../../model/cart.model';
import { DefaultValue, FeatureType, Language } from '../../model/constant';
import { ImageSizingModel } from '../../model/images/image-sizing-model';
import { RoomConditionDto, Surcharge } from '../../model/products/product-room-detail.model';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { AppConfigService } from '../../services/app-config/app-config.service';
import { CartService } from '../../services/cart/cart.service';
import { DialogConfirmComponent } from '../modal/dialog-confirm/dialog-confirm.component';
import { DialogConfirmType } from '../modal/dialog-confirm/dialog-confirm.model';
import { CartOptionModel, TermsCondition, TermsConditionsTemplate, TermsConditionsType } from './cart-option.model';
import { DialogCartSummaryComponent } from './dialog-cart-summary/dialog-cart-summary.component';
import { Currency } from 'app/model/currency.model';
import { CurrencyService } from 'app/services/currency/currency.service';
import { CheckoutService } from 'app/services/checkout/checkout.service';
import { OccupancyInfoDTO } from 'app/model/checkout/guest-info.model';
import { DialogTaxesFeesComponent } from './dialog-taxes-fees/dialog-taxes-fees.component';
import { DialogUsePromotionCodeComponent } from './dialog-use-promotion-code/dialog-use-promotion-code.component';
import { PromotionCodeResponse, ValidatePromotionCodeResponse } from 'app/services/promotion-code/model/promotion-code.model';
import { ActivatedRoute } from '@angular/router';
import { redirectURIV2 } from 'environments/properties';

@Component({
  selector: 'act-cart',
  templateUrl: './cart.component.html',
  styleUrls: ['./cart.component.scss']
})
export class CartComponent implements OnInit, OnDestroy, AfterViewInit, AfterViewChecked {

  private option$ = new BehaviorSubject<CartOptionModel>(new CartOptionModel());

  @Input()
  set options(value) { this.option$.next(value); }
  get options() { return this.option$.getValue(); }

  @Input() multiPaymentMainChannel;
  @Output()
  onCartButtonClick = new EventEmitter();
  @Output()
  onClickMore = new EventEmitter();
  @Output() onSelectPromotionCode = new EventEmitter<PromotionCodeResponse>();

  cartSubscription: Subscription;
  subscriptionTotalMultipaymentDetail: Subscription;
  subscriptionDialog: Subscription;
  messageSubscription: Subscription;

  cart: CartModel;
  multiPaymentDetail: CartMultipaymentDetail;
  message: { text: string, code: string, action: string };
  isCartButtonClicked = false;

  private langId = this.appConfigService.getLanguageId();
  public lang = this.appConfigService.getLanguage();
  public langType = Language;
  private hotelImage: string;
  private bookNight = 1;
  private grandTotalPrice = 0;
  private totalRoomSelected = 0;
  private formatSearchDate = this.appConfigService.dateHumanFormat; // tslint:disable-line
  private dataSelenium: any = {};
  private dialogRef: MdDialogRef<DialogCartSummaryComponent>;
  public totalAdult = 0;
  public totalChild = 0;
  public dialogSummaryOpening = false;
  public isMobileShowFullCart = false;
  public allowMultiPayment = false;
  public isAllowMultiCorporateFleetOnly = false;
  public messageError = {
    payOwn: false,
    multiPayment: false
  }
  public occupancyInfo: OccupancyInfoDTO;
  public totalTaxesAndFees: number = 0;
  public termsConditions: TermsCondition = new TermsCondition();
  public TermsConditionTemplate = TermsConditionsTemplate;
  public displaySurcharges: boolean = false;
  public displayCurrencyNote: boolean = false;
  public promoCode?: PromotionCodeResponse;
  public validatedPromoCode?: ValidatePromotionCodeResponse;
  public isLockedPromoCode: boolean = false;

  public defaultCurrency = DefaultValue.CURRENCY;
  public currency: Currency;
  private currencySubscription: Subscription;
  private mobileMode: boolean = false;

  @ViewChild('miniCart') public miniCart: ElementRef;

  constructor(
    private cartService: CartService,
    private appConfigService: AppConfigService,
    private utilitiesService: UtilitiesService,
    private titlecasePipe: TitleCasePipe,
    private decimalPipe: DecimalPipe,
    private translatePipe: TranslatePipe,
    private dialog: MdDialog,
    private productService: ProductService,
    private userService: UserService,
    private authorizeService: AuthorizeService,
    private changeDetectorRef: ChangeDetectorRef,
    private currencyService: CurrencyService,
    private checkoutService: CheckoutService,
    private route: ActivatedRoute
  ) { }

  ngOnInit() {
    this.subscribeCurrency();
    this.hotelImage = ImageSizingModel.defaultImage().thumbnail['1x'];
    this.allowMultiPayment = this.productService.allowMultiPayment(this.userService.getUser());
    let notAllowMultiPaymentCreditTerms;
    let allowMultiPaymentCorporateFleet;
    this.userService.getUser().features
      .map(f => {
        if (f.feature === FeatureType.ALLOW_CREDIT_TERMS && !f.allow || f.feature === FeatureType.ALLOW_MULTI_PAYMENT && !f.allow) {
          notAllowMultiPaymentCreditTerms = true;
        }
        if (f.feature === FeatureType.ALLOW_MULTI_PAYMENT_CORPORATE_FLEET && f.allow) {
          allowMultiPaymentCorporateFleet = true;
        }
      });

      this.isAllowMultiCorporateFleetOnly = notAllowMultiPaymentCreditTerms && allowMultiPaymentCorporateFleet;

    this.cartSubscription = this.cartService.getTempCart$()
      .subscribe(items => {
        this.onCartItemChanged(items);
      });

    this.subscriptionTotalMultipaymentDetail = this.cartService.getCartMultiPaymentDetail()
      .subscribe(detail => {
        this.multiPaymentDetail = detail;
      });

    this.messageSubscription = this.cartService.getCartMessage()
      .subscribe(msg => {
        this.message = msg;

        if (this.message && this.message.code === '2004') {
          this.messageError.payOwn = true
        }

        if (this.message && this.message.code === '2005') {
          this.messageError.multiPayment = true
        }
      });

    this.appConfigService.getLanguageObs()
      .subscribe(lang => {
        this.lang = lang;
        this.setSpecialTermsAndConditions();
      });

    this.checkoutService.currentValidatedPromoCode.subscribe(validatedPromoCode => {
      this.updatePromotionCode();
    });

    this.checkoutService.currentIsLockedPromoCode.subscribe(isLocked => {
      this.isLockedPromoCode = isLocked;
    });
  }

  ngAfterViewInit() {
    this.changeDetectorRef.detectChanges();
  }

  ngAfterViewChecked() {
    this.mobileMode = this.utilitiesService.isMobile();
  }

  ngOnDestroy() {
    this.cartSubscription && this.cartSubscription.unsubscribe();
    this.subscriptionDialog && this.subscriptionDialog.unsubscribe();
    this.currencySubscription && this.currencySubscription.unsubscribe();
    this.dialogRef && this.dialogRef.close();
  }

  getTotalRoom() {
    return this.totalRoomSelected;
  }

  getGrandTotal() {
    return this.grandTotalPrice;
  }

  setCurrencyFormat(amount: number) {
    return this.utilitiesService.insertCurrencySymbol(amount);
  }

  setRoomTitleize(conditions: RoomConditionDto[]) {
    conditions.map(condition => {
      condition.displayTitle = this.utilitiesService.titleize(condition.title);
      return condition;
    });
  }

  onCartItemChanged(cart) {
    this.cart = cart;
    this.bookNight = this.cartService.calculateBookNight(this.cart.search.checkInDate, this.cart.search.checkOutDate);
    if (this.cart.conditions.length > 0) {
      if (this.cart.optionDetail !== undefined) {
        let conditionIndex = 0;
        this.cart.optionDetail.optionList.forEach(option => {
          option.conditionList.forEach(condition => {
            if (condition.titles.th != null && condition.titles.en != null) {
              option.title = this.lang === this.lang ?
                option.title + " (" + condition.titles.th + ")" :
                option.title + " (" + condition.titles.en + ")";
              this.cart.conditions[conditionIndex].displayTitle = this.utilitiesService.titleize(option.title);
            } else {
              option.title = option.title;
              this.cart.conditions[conditionIndex].displayTitle = this.utilitiesService.titleize(option.title);
            }
            conditionIndex++;
          });
        });
      } else {
        this.setRoomTitleize(this.cart.conditions);
      }
      this.cartService.calculateTotalPrice(this.cart.conditions, this.bookNight);
      this.totalRoomSelected = this.cartService.calculateTotalRoomSelected(this.cart.conditions);
      this.grandTotalPrice = this.cartService.calculateGrandTotalPrice(this.cart.conditions, this.validatedPromoCode);
      this.totalAdult = this.cartService.calculateTotalAdult(this.cart.conditions);
      this.totalChild = this.cartService.calculateTotalChild(this.cart.conditions);
    }
    if (cart.productDetail && cart.productDetail.image) {
      this.hotelImage = cart.productDetail.image['1x'];
    }

    this.dataSelenium.checkInDate = this.utilitiesService.reverseFormatDate(this.cart.search.checkInDate);
    this.dataSelenium.checkOutDate = this.utilitiesService.reverseFormatDate(this.cart.search.checkOutDate);

    this.cart.conditions.map(condition => {
      condition.surcharges && (condition.displayIncludeSurcharge = this.surcharge(condition.surcharges, 'include'));
      condition.surcharges && (condition.displayExcludeSurcharge = this.surcharge(condition.surcharges, 'exclude'));
      this.displaySurcharges = condition.surcharges && condition.surcharges.length > 0;
      return condition;
    });
    this.messageError.payOwn = false;
    this.messageError.multiPayment = false;

    this.updateOccupancyInfo();
  }

  updateOccupancyInfo() {
    let adult = 0;
    let child = 0;
    let childAges = [];
    this.cart.optionDetail.roomOccupancy.forEach(roomOccupancy => {
      adult += roomOccupancy.adult.valueOf();
      child += roomOccupancy.child.valueOf();
      childAges.push(...roomOccupancy.childAge);
    });
    this.occupancyInfo = this.checkoutService.transformOccupancyInfo(adult, child, childAges);
    this.setSpecialTermsAndConditions();
    this.calculateTotalPriceExcludeAndTaxesAndFees();
    this.displayCurrencyNote = this.currency.currencyCode !== this.defaultCurrency.currencyCode;
  }

  onCartButtonClicked() {
    this.isCartButtonClicked = true;
    this.onCartButtonClick.emit(this.cart);
  }

  surcharge(surcharges: Surcharge[], type: String): String {
    let result = '';
    let count = 0;
    for (const surcharge of surcharges) {
      if (surcharge.charge == type) {
        if (count > 0) {
          result = result.concat(', ');
        }
        const defaultSurchargePriceString = this.decimalPipe.transform(surcharge.price, '1.2-2');
        result = result.concat(`${surcharge.name.toString()} ${DefaultValue.CURRENCY.currencyCode} ${defaultSurchargePriceString}`);
        if (this.currency.currencyCode !== DefaultValue.CURRENCY.currencyCode) {
          const surchargePrice = this.currencyService.transformCurrentCurrencyFromTHB(surcharge.price, false);
          const surchargePriceString = this.decimalPipe.transform(surchargePrice, '1.2-2');
          result = result.concat(` (≈ ${this.currency.currencyCode} ${surchargePriceString})`);
        }
        count++;
      }
    }

    result && (result = this.translatePipe.transform(type == 'include' ? 'cart-surcharge-include' : 'cart-surcharge-exclude').replace('{text}', result));

    return result;
  }

  openDialog() {
    if (this.cart && this.cart.conditions && this.cart.conditions.length > 0 && !this.dialogSummaryOpening) {
      this.dialogSummaryOpening = true;
      this.onClickMore.emit();
      this.dialogRef = this.dialog.open(DialogCartSummaryComponent, {
        position: {
          top: '0px',
          bottom: '0px'
        },
        hasBackdrop: false
      });
      this.dialogRef.componentInstance.data = {
        formatSearchDate: this.formatSearchDate,
        cart: this.cart,
        bookNight: this.bookNight,
        multiPaymentDetail: this.multiPaymentDetail,
        grandTotalPrice: this.grandTotalPrice,
        message: this.message,
        multiPaymentMainChannel: this.multiPaymentMainChannel,
        isAllowMultiCorporateFleetOnly: this.isAllowMultiCorporateFleetOnly
      };
      this.subscriptionDialog && this.subscriptionDialog.unsubscribe();
      this.subscriptionDialog = this.dialogRef.afterClosed().subscribe(() => this.dialogSummaryOpening = false);
    } else {
      this.dialogRef && this.dialogRef.close();
    }
  }

  public closeDialogSummaryIfExist() {
    this.dialogSummaryOpening = false;
    this.dialogRef && this.dialogRef.close();
  }

  onCancelShareBudget() {
    const message = { text: this.translatePipe.transform('dialog-cancel-share-budget-text'), subText: this.translatePipe.transform('dialog-cancel-share-budget-sub-text') };
    const dialogRef: MdDialogRef<DialogConfirmComponent> = this.dialog.open(DialogConfirmComponent,
      {
        data: {
          confirmType: DialogConfirmType.CONFIRM_CANCEL_SHARE_BUDGET_FROM_USER,
          data: {
            message: message
          }
        },
      });
    dialogRef.afterClosed().subscribe(res => {
      if (res) {
        this.authorizeService.removeCoBookerCookie();
        this.cartService.updateCartCancelShareBudget();
      }
    });
  }

  subscribeCurrency() {
    this.currency = this.currencyService.getCurrency();
    this.currencySubscription = this.currencyService.getCurrencyObs().subscribe(currency => {
      this.currency = currency;
      this.onCartItemChanged(this.cart);
    });
  }

  setSpecialTermsAndConditions(): void {
    const specialTermsAndConditions = this.cart.specialTermsAndConditions;
    if (specialTermsAndConditions) {
      const types = specialTermsAndConditions.map((item) => item.type);

      if (types.includes(TermsConditionsType.SUPPLIER)) {
        this.termsConditions.template = TermsConditionsTemplate.SUPPLIER;
        const supplier = specialTermsAndConditions.find((item) => item.type === TermsConditionsType.SUPPLIER);
        this.termsConditions.linkSupplier = this.lang === this.langType.en ? supplier.linkEn : supplier.linkTh;

        if (types.includes(TermsConditionsType.AGENT)) {
          this.termsConditions.template = TermsConditionsTemplate.SUPPLIER_AGENT;
          const agent = specialTermsAndConditions.find((item) => item.type === TermsConditionsType.AGENT);
          this.termsConditions.linkAgent = this.lang === this.langType.en ? agent.linkEn : agent.linkTh;
        }
      }
    }
  }

  calculateTotalPriceExcludeAndTaxesAndFees(): void {
    this.totalTaxesAndFees = 0;
    this.cart.conditions.forEach(each => {
      const totalPrice = Number(each.totalPrice) * Number(each.quantity);
      let totalPriceExclude = totalPrice;

      if (each.taxesAndFees) {
        const taxesAndFees = each.taxesAndFees * Number(each.quantity);
        totalPriceExclude = totalPrice - taxesAndFees;
        this.totalTaxesAndFees += taxesAndFees;
      }

      each.totalPriceExclude = parseFloat(totalPriceExclude.toFixed(2));
      this.totalTaxesAndFees = parseFloat(this.totalTaxesAndFees.toFixed(2));
    });
  }

  openDialogTaxesAndFees() {
    if (this.mobileMode) {
      const dialogRef = this.dialog.open(DialogTaxesFeesComponent);
    }
  }

  openDialogUsePromotionCode() {
    if (!this.isLockedPromoCode) {
      const dialogRef = this.dialog.open(DialogUsePromotionCodeComponent);
      dialogRef.afterClosed().subscribe((response: { type: string; data?: any }) => {
      if (response.type != 'close') {
          if (response.type == 'submit') {
            this.onSelectPromotionCode.emit(response.data);
        } else if (response.type == 'error') {
          if (response.data === '4723') {
            const hotelParam = this.route.snapshot.queryParamMap.get('hotel');
            if (hotelParam) {
              const decoded = decodeURIComponent(hotelParam);
              window.location.href = `${redirectURIV2}${decoded}`;
            }
          }
        }
        }
      });
    }
  }

  updatePromotionCode() {
    const { codeInfo, validateInfo } = this.checkoutService.getCurrentPromotionCode();
    this.promoCode = codeInfo;
    this.validatedPromoCode = validateInfo;
    this.updateGrandTotal();
  }

  updateGrandTotal() {
    if (this.cart) {
      this.grandTotalPrice = this.cartService.calculateGrandTotalPrice(this.cart.conditions, this.validatedPromoCode);
      this.checkoutService.updateGrandTotalPrice(this.grandTotalPrice);
    }
  }

}



// WEBPACK FOOTER //
// ./src/app/components/cart/cart.component.ts